import Component from '@glimmer/component'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { isEmpty, isPresent } from '@ember/utils'
import { htmlSafe } from '@ember/template'
import { task, timeout } from 'ember-concurrency'
import type { Task } from 'ember-concurrency'
import { tableColumnsToComparerKeyMapping, injectComparerKeys } from '@blakeelearning/data-tables/utils/sorting'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { Product } from 'teacher-ui-client/utils/products'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import {
  ReadingEggsReadingProgressHandler,
  ReadingEggsSpellingProgressHandler,
  ReadingEggsStorylandsProgressHandler,
  ReadingEggspressReadingProgressHandler,
  ReadingEggspressSpellingProgressHandler,
  FastPhonicsPeaksProgressHandler,
  MathseedsLessonsProgressHandler,
  MathseedsMentalMinuteProgressHandler,
  MathseedsPrimeLessonsProgressHandler,
  WritingLegendsLessonsProgressHandler,
} from 'teacher-ui-client/progress-handler/utils'
import type FeatureControlService from 'teacher-ui-client/services/feature-control'
import type { ProgressHandler, ProgressionRecord } from 'teacher-ui-client/progress-handler/utils'
import type ProgressHandlerService from 'teacher-ui-client/services/progress-handler'
import type DataTransformer from '@blakeelearning/data-tables/services/data-transformer'
import { type IntlService, t } from 'ember-intl'
import type Features from 'teacher-ui-client/services/features'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import type ProgressionSequenceService from 'teacher-ui-client/services/progression-sequence'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type SessionService from 'teacher-ui-client/services/session'
import type Student from 'teacher-ui-client/models/student'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type { SafeString } from '@ember/template'
import type RouterService from '@ember/routing/router-service'
import type { GroupFilter, GroupFilterOption } from 'teacher-ui-client/components/tables/progress-table/group-filter'
import type {
  CustomPipelineDescriptor,
  FilterPipelineDescriptor,
  PaginationPipelineDescriptor,
  PipelineDescriptor,
  SortingPipelineDescriptor,
} from '@blakeelearning/data-tables/utils/data-transformer'
import type { SortingConfig } from '@blakeelearning/data-tables/utils/sorting'
import type { PaginationConfig } from '@blakeelearning/data-tables/utils/paging'
import type { FilterDataItem } from '@blakeelearning/data-tables/utils/filtering'
import { TabGroupComponent } from 'teacher-ui-client/components/tab-group'
import BasicTable from 'teacher-ui-client/components/stable-table/basic-table'
import SortableCell from 'teacher-ui-client/components/stable-table/basic-cells/sortable'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import { ProgressTableGroupFilter } from 'teacher-ui-client/components/tables/progress-table/group-filter'
import applyTransform from '@blakeelearning/data-tables/modifiers/apply-transform'
import perform from 'ember-concurrency/helpers/perform'
import { and, not, or } from 'ember-truth-helpers'
import { array, hash, concat, fn } from '@ember/helper'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { TAB_TYPE_KEYS } from 'teacher-ui-client/components/tab-group/tab-types'
import { uniqueId } from '@ember/helper'
import { on } from '@ember/modifier'
import { optional } from 'teacher-ui-client/helpers/optional'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { includes } from 'teacher-ui-client/helpers/includes'
import BasePageSelector from 'teacher-ui-client/components/base/page-selector'
import { hasId } from 'teacher-ui-client/utils/type-guards'
import type TeacherEvents from 'teacher-ui-client/services/teacher-events'
import { debounce } from 'lodash'

function positionLabelFor(handler: ProgressHandler, progressionRecords: ProgressionRecord[]) {
  return {
    [handler.progressType]: handler.getSavedOption(progressionRecords)?.positionLabel,
  }
}

const colorClassNames: Partial<Record<SubscriptionType, Record<PropertyKey, string>>> = {
  [SubscriptionType.Reading]: {
    groupedHeaderBg: 'bg-oceany-blue-150',
    headerBg: 'bg-oceany-blue-100',
    cellBg: 'bg-oceany-blue-50',
    rowGroupHoverBg: 'group-hover:bg-oceany-blue-100',
  },
  [SubscriptionType.Maths]: {
    groupedHeaderBg: 'bg-ms-green-150',
    headerBg: 'bg-ms-green-100',
    cellBg: 'bg-ms-green-50',
    rowGroupHoverBg: 'group-hover:bg-ms-green-100',
  },
  [SubscriptionType.Writing]: {
    groupedHeaderBg: 'bg-wl-blue-100',
    headerBg: 'bg-wl-blue-100',
    cellBg: 'bg-wl-blue-50',
    rowGroupHoverBg: 'group-hover:bg-wl-blue-100',
  },
}

type FlattenProgressionData = (progressionRecords: ProgressionRecord[]) => Record<PropertyKey, unknown>

type FilterPredicate = (item: FilterDataItem) => boolean

interface GroupFilterFnArgs {
  groupFilter: GroupFilter
  isItemSelected: FilterPredicate
}

interface TextFilterFnArgs {
  textFilter: string
  dataKeysToFilterOn: string[]
  isItemSelected: FilterPredicate
}

interface StudentViewModel {
  id: string
  firstName: string
  lastName: string
  gradeName?: string
  gradePosition: number
  schoolClasses: SchoolClass[]
  /**
   * for each progress data value
   */
  [progressType: string]: unknown
}

interface Args {
  items: Student[]
  allSchoolClasses?: SchoolClass[]
  subscriptionType: SubscriptionType
  selectedItemIds?: string[]
  updateProgressesTask?: Task<unknown, unknown[]>
  sortingConfig?: SortingConfig
  paginationConfig?: PaginationConfig
  textFilter?: string
  exportable?: boolean
  setTextFilter?: (event: Event & { target: HTMLInputElement }) => unknown
  onSortClick?: (sortObject: SortObject) => unknown
  onBatchEditClick?: () => unknown
  onItemSelect?: ({ id }: { id: string }) => unknown
  onItemSelectAll?: (items: { id: string }[]) => unknown
  onItemButtonClick?: ({ id }: { id: string }) => unknown
}

interface Signature {
  Args: Args
}

export class ProgressTable extends Component<Signature> {
  @service dataTransformer!: DataTransformer

  @service intl!: IntlService

  @service features!: Features

  @service progressHandler!: ProgressHandlerService

  @service studentProgress!: StudentProgressService

  @service lessonInfo!: LessonInfoService

  @service progressionSequence!: ProgressionSequenceService

  @service gradeSets!: GradeSetsService

  @service session!: SessionService

  @service router!: RouterService

  @service featureControl!: FeatureControlService

  @service teacherEvents!: TeacherEvents

  @queryParam('manage/progress') product!: Product

  get selectedItemIds() {
    return this.args.selectedItemIds ?? []
  }

  @tracked selectableItems: StudentViewModel[] = []

  @tracked transformedItems: StudentViewModel[] = []

  @tracked pageCount = 1

  @tracked groupFilterOption: Nullable<GroupFilterOption> = null

  get isSubco() {
    return this.session.currentTeacher.subcoFor(this.args.subscriptionType)
  }

  get groupFilter() {
    if (this.groupFilterOption) return this.groupFilterOption.filter
    const schoolClasses = this.isSubco ? this.args.allSchoolClasses : this.mySchoolClasses
    return schoolClasses ?? []
  }

  get mySchoolClasses(): SchoolClass[] {
    return this.session.currentTeacher.schoolClassesForSubscriptionType(this.args.subscriptionType)
  }

  get mathseedsPrimeEnabled() {
    return this.featureControl.canAccess('msp_product')
  }

  get productTabs(): Product[] {
    switch (this.args.subscriptionType) {
      case SubscriptionType.Reading:
        return [Product.RE, Product.REX, Product.FP]
      case SubscriptionType.Maths:
        if (this.mathseedsPrimeEnabled) {
          return [Product.MS, Product.MSP]
        } else {
          return [Product.MS]
        }
      case SubscriptionType.Writing:
        return [Product.WL]
      default:
        return []
    }
  }

  /**
   * Returns an array of operation steps that will be applied to the items in order
   */
  get pipelineDescriptors(): Nullable<PipelineDescriptor>[] {
    return [
      this.rowDescriptor,
      this.sortingDescriptor,
      this.groupFilterDescriptor,
      this.textFilterDescriptor,
      this.paginationDescriptor,
    ]
  }

  /**
   * Ensures the needed lesson info & progress is loaded for a given product and students. Only loaded if not cached.
   *
   * Returns a function (that relies on the loaded data) to get the formatted progress value labels from a students'
   * progression records, for the given product. These progress labels are keyed by precinct.
   */
  async progressLabelFunction(product: Product, remoteIds: string[]): Promise<FlattenProgressionData> {
    switch (product) {
      case Product.RE:
        await this.lessonInfo.loadLessonInfoFor([Precinct.RE_READING, Precinct.RE_SPELLING, Precinct.RE_STORYLANDS])
        await this.studentProgress.ensureProgresses([
          { ...ReadingEggsReadingProgressHandler.payloadAttributes, remoteIds },
          { ...ReadingEggsSpellingProgressHandler.payloadAttributes, remoteIds },
          { ...ReadingEggsStorylandsProgressHandler.payloadAttributes, remoteIds },
        ])
        return (progressionRecords: ProgressionRecord[]) => ({
          ...positionLabelFor(this.progressHandler.readingEggsReadingHandler, progressionRecords),
          ...positionLabelFor(this.progressHandler.readingEggsSpellingHandler, progressionRecords),
          ...positionLabelFor(this.progressHandler.readingEggsStorylandsHandler, progressionRecords),
        })
      case Product.REX:
        await this.lessonInfo.loadLessonInfoFor([Precinct.REX_READING, Precinct.REX_SPELLING])
        await this.studentProgress.ensureProgresses([
          { ...ReadingEggspressReadingProgressHandler.payloadAttributes, remoteIds },
          { ...ReadingEggspressSpellingProgressHandler.payloadAttributes, remoteIds },
        ])
        return (progressionRecords: ProgressionRecord[]) => ({
          ...positionLabelFor(this.progressHandler.readingEggspressReadingHandler, progressionRecords),
          ...positionLabelFor(this.progressHandler.readingEggspressSpellingHandler, progressionRecords),
        })
      case Product.FP:
        await this.lessonInfo.loadLessonInfoFor([Precinct.FP_PEAKS])
        await this.studentProgress.ensureProgresses([
          { ...FastPhonicsPeaksProgressHandler.payloadAttributes, remoteIds },
        ])
        return (progressionRecords: ProgressionRecord[]) => ({
          ...positionLabelFor(this.progressHandler.fastPhonicsPeaksHandler, progressionRecords),
        })
      case Product.MS:
        await this.lessonInfo.loadLessonInfoFor([Precinct.MS_LESSONS, Precinct.MS_MENTAL_MINUTE])
        await this.studentProgress.ensureProgresses([
          { ...MathseedsLessonsProgressHandler.payloadAttributes, remoteIds },
          { ...MathseedsMentalMinuteProgressHandler.payloadAttributes, remoteIds },
        ])
        return (progressionRecords: ProgressionRecord[]) => ({
          ...positionLabelFor(this.progressHandler.mathseedsLessonsHandler, progressionRecords),
          ...positionLabelFor(this.progressHandler.mathseedsMentalMinuteAddSubHandler, progressionRecords),
          ...positionLabelFor(this.progressHandler.mathseedsMentalMinuteMulDivHandler, progressionRecords),
        })
      case Product.MSP:
        await this.progressionSequence.loadSequence(Product.MSP)
        // getting the progress for MSP students requires curriculum
        await this.studentProgress.ensureProgresses([
          {
            ...MathseedsPrimeLessonsProgressHandler.payloadAttributes,
            curriculum: this.progressHandler.curriculum,
            remoteIds,
          },
        ])
        return (progressionRecords: ProgressionRecord[]) => ({
          ...positionLabelFor(this.progressHandler.mathseedsPrimeNumberHandler, progressionRecords),
          ...positionLabelFor(this.progressHandler.mathseedsPrimeFractionsHandler, progressionRecords),
          ...positionLabelFor(this.progressHandler.mathseedsPrimeOperationsHandler, progressionRecords),
          ...positionLabelFor(this.progressHandler.mathseedsPrimeGeometryHandler, progressionRecords),
          ...positionLabelFor(this.progressHandler.mathseedsPrimeMeasurementHandler, progressionRecords),
        })
      case Product.WL:
        await this.lessonInfo.loadLessonInfoFor([Precinct.WL_WRITING])
        await this.studentProgress.ensureProgresses([
          { ...WritingLegendsLessonsProgressHandler.payloadAttributes, remoteIds },
        ])
        return (progressionRecords: ProgressionRecord[]) => ({
          ...positionLabelFor(this.progressHandler.writingLegendsWritingHandler, progressionRecords),
        })
      default:
        return () => ({})
    }
  }

  get rowDescriptor(): CustomPipelineDescriptor {
    const lastUpdate = this.args.updateProgressesTask?.last
    const transformer = async ({ items, ...rest }: { items: Student[] }) => {
      // If progress was saved and is currently updating, wait for it
      await lastUpdate

      const itemsWithIds = items.filter(hasId)
      const studentIds = itemsWithIds.map((s) => s.id)
      const getPositionLabelsFor: FlattenProgressionData = await this.progressLabelFunction(this.product, studentIds)

      return {
        ...rest,
        items: itemsWithIds.map(({ id, firstName, lastName, gradePosition, schoolClasses }) => ({
          id,
          firstName,
          lastName,
          gradeName: this.gradeSets.findByPosition(gradePosition)?.name,
          gradePosition,
          schoolClasses,
          ...getPositionLabelsFor(this.studentProgress.allProgressFor(id)),
        })),
      }
    }
    return { type: 'custom', transformer }
  }

  get sortingDescriptor(): Nullable<SortingPipelineDescriptor> {
    const { sortingConfig } = this.args
    const tableColumns = this.columns

    if (!sortingConfig) return null

    const comparerKeyMapping = tableColumnsToComparerKeyMapping(tableColumns)
    const sortingConfigWithComparerKeys = injectComparerKeys(sortingConfig, comparerKeyMapping)
    return { type: 'sort', config: sortingConfigWithComparerKeys }
  }

  get groupFilterDescriptor(): Nullable<FilterPipelineDescriptor> {
    const { groupFilter } = this

    // Create a filter function with the given text, keys and a function to determine selected items
    const isItemSelected: FilterPredicate = (item) =>
      typeof item.id === 'string' && this.selectedItemIds?.includes(item.id)
    const itemFilter = groupFilterFunction({ groupFilter, isItemSelected })
    const filterConfig = { itemFilter }

    return { type: 'filter', config: filterConfig }
  }

  get textFilterDescriptor(): Nullable<FilterPipelineDescriptor> {
    const { columns } = this
    const { textFilter } = this.args

    // Only need to apply a text filter if there is text, and if there are data keys to filter on
    const dataKeysToFilterOn = columns.map<string>((c) => c.valuePath?.toString()).filter(isPresent)
    if (!textFilter || !dataKeysToFilterOn || isEmpty(textFilter) || isEmpty(dataKeysToFilterOn)) return null

    // Create a filter function with the given text, keys and a function to determine selected items
    const isItemSelected: FilterPredicate = (item) =>
      typeof item.id === 'string' && this.selectedItemIds?.includes(item.id)
    const itemFilter = textFilterFunction({ textFilter, dataKeysToFilterOn, isItemSelected })
    const filterConfig = { itemFilter }

    return { type: 'filter', config: filterConfig }
  }

  get paginationDescriptor(): Nullable<PaginationPipelineDescriptor> {
    if (!this.args.paginationConfig) return null
    return { type: 'paginate', config: this.args.paginationConfig }
  }

  /**
   * This task provides an array of items and operations to the data transformer service, which will asynchronously
   * transform the data according to the operations, then set those transformed items for display in the table
   */
  transformItems = task({ restartable: true }, async (items: Student[] = [], pipeline: PipelineDescriptor[] = []) => {
    await timeout(200) // debounce

    const presentDescriptors = pipeline.filter(isPresent)
    const transformedData = await this.dataTransformer.buildAndTransform<Student, StudentViewModel>(
      items,
      presentDescriptors,
    )

    this.pageCount = transformedData.pageCount // for the pager component
    // used to determine item scope for the select all checkbox, to include items on other pages (but NOT filtered out items)
    const allPagedItems = transformedData.paginated?.flat()
    const selectableItems = allPagedItems || transformedData.items
    this.selectableItems = selectableItems

    this.transformedItems = transformedData.items
  })

  /**
   * This is only comparing lengths, you could individually match ids, but this should suffice, since all selected items
   * should always be within the selectable items scope, so the only way the lengths can match is if they are the same.
   * Note: can't use equal computed, since two properties
   *
   * returns true if all selectable items are selected
   */
  get isAllSelected() {
    return this.selectedItemIds.length === this.selectableItems.length
  }

  get isNoneSelected() {
    return this.selectedItemIds.length === 0
  }

  get colorClassNames() {
    return colorClassNames[this.args.subscriptionType] ?? {}
  }

  get sortColumn() {
    const { headerBg, cellBg, rowGroupHoverBg } = this.colorClassNames
    return {
      component: SortableCell,
      comparerKey: 'sortByNumber',
      classNames: `table-box table-box-header ${headerBg}`,
      cellClassNames: `table-box table-box-cell ${cellBg} ${rowGroupHoverBg}`,
    }
  }

  get studentColumns() {
    const { sortColumn, intl } = this
    return [
      {
        ...sortColumn,
        value: intl.t('tables.progress.headers.firstName'),
        valuePath: 'firstName',
        comparerKey: 'sortByValue',
        cellClassNames: `${sortColumn.cellClassNames} max-w-48 text-ellipsis overflow-clip`,
      },
      {
        ...sortColumn,
        value: intl.t('tables.progress.headers.lastName'),
        valuePath: 'lastName',
        comparerKey: 'sortByValue',
        cellClassNames: `${sortColumn.cellClassNames} max-w-48 text-ellipsis overflow-clip`,
      },
      {
        ...sortColumn,
        value: intl.t('tables.progress.headers.gradeName'),
        valuePath: 'gradeName',
        sortKey: 'gradePosition',
      },
    ]
  }

  get progressColumns() {
    const { sortColumn, intl } = this

    switch (this.product) {
      case Product.RE:
        return [
          {
            ...sortColumn,
            value: intl.t('tables.progress.headers.re.lessons'),
            valuePath: this.progressHandler.readingEggsReadingHandler.progressType,
          },
          {
            ...sortColumn,
            value: intl.t('tables.progress.headers.re.spelling'),
            valuePath: this.progressHandler.readingEggsSpellingHandler.progressType,
          },
          {
            ...sortColumn,
            value: intl.t('tables.progress.headers.re.clinkerCastle'),
            valuePath: this.progressHandler.readingEggsStorylandsHandler.progressType,
          },
        ]
      case Product.REX:
        return [
          {
            ...sortColumn,
            value: intl.t('tables.progress.headers.rex.lessons'),
            valuePath: this.progressHandler.readingEggspressReadingHandler.progressType,
          },
          {
            ...sortColumn,
            value: intl.t('tables.progress.headers.rex.spelling'),
            valuePath: this.progressHandler.readingEggspressSpellingHandler.progressType,
          },
        ]
      case Product.FP:
        return [
          {
            ...sortColumn,
            value: intl.t('tables.progress.headers.fp.lessons'),
            valuePath: this.progressHandler.fastPhonicsPeaksHandler.progressType,
          },
        ]
      case Product.MS:
        return [
          {
            ...sortColumn,
            value: intl.t('tables.progress.headers.maths.lessons'),
            valuePath: this.progressHandler.mathseedsLessonsHandler.progressType,
          },
          {
            ...sortColumn,
            value: htmlSafe(
              `${intl.t('tables.progress.headers.maths.mentalMinute.title')}
              <br/>
              ${intl.t('tables.progress.headers.maths.mentalMinute.addSub')}`,
            ),
            valuePath: this.progressHandler.mathseedsMentalMinuteAddSubHandler.progressType,
          },
          {
            ...sortColumn,
            value: htmlSafe(
              `${intl.t('tables.progress.headers.maths.mentalMinute.title')}
              <br/>
              ${intl.t('tables.progress.headers.maths.mentalMinute.divMult')}`,
            ),
            valuePath: this.progressHandler.mathseedsMentalMinuteMulDivHandler.progressType,
          },
        ]
      case Product.MSP:
        return [
          {
            ...sortColumn,
            value: intl.t('mathseedsPrimeThreads.number.name'),
            valuePath: this.progressHandler.mathseedsPrimeNumberHandler.progressType,
            comparerKey: 'sortByValue',
          },
          {
            ...sortColumn,
            value: intl.t('mathseedsPrimeThreads.fractions.name'),
            valuePath: this.progressHandler.mathseedsPrimeFractionsHandler.progressType,
            comparerKey: 'sortByValue',
          },
          {
            ...sortColumn,
            value: intl.t('mathseedsPrimeThreads.operations.name'),
            valuePath: this.progressHandler.mathseedsPrimeOperationsHandler.progressType,
            comparerKey: 'sortByValue',
          },
          {
            ...sortColumn,
            value: intl.t('mathseedsPrimeThreads.geometry.name'),
            valuePath: this.progressHandler.mathseedsPrimeGeometryHandler.progressType,
            comparerKey: 'sortByValue',
          },
          ...(this.features.isEnabled('tui_ms_prime_progress_measurement')
            ? [
                {
                  ...sortColumn,
                  value: intl.t('mathseedsPrimeThreads.measurement.name'),
                  valuePath: this.progressHandler.mathseedsPrimeMeasurementHandler.progressType,
                },
              ]
            : []),
        ]
      case Product.WL:
        return [
          {
            ...sortColumn,
            value: intl.t('tables.progress.headers.wl.lessons'),
            valuePath: this.progressHandler.writingLegendsWritingHandler.progressType,
          },
        ]
      default:
        return []
    }
  }

  get columns(): Record<PropertyKey, string | SafeString | boolean | typeof SortableCell>[] {
    const { headerBg, cellBg, rowGroupHoverBg } = this.colorClassNames

    const selectAllColumn = {
      isSelectColumn: true,
      classNames: `table-box table-box-header ${headerBg} text-center w-3`,
      cellClassNames: `table-box table-box-cell ${cellBg} ${rowGroupHoverBg} cursor-pointer text-center`,
    }

    const editColumn = {
      isEditColumn: true,
      classNames: `table-box table-box-header ${headerBg} w-32`,
      cellClassNames: `table-box table-box-cell ${cellBg} ${rowGroupHoverBg} text-center`,
    }

    return [selectAllColumn, ...this.studentColumns, ...this.progressColumns, editColumn]
  }

  @action
  setGroupFilterOption(groupFilterOption: GroupFilterOption) {
    this.groupFilterOption = groupFilterOption
    void this.router.transitionTo({ queryParams: { pageNumber: 1 } })
  }

  debouncedSendTeacherEvent = debounce(() => {
    this.teacherEvents.sendEvent('manage-progress-search-used', this.product, {
      teacherId: this.session.currentTeacher.id,
    })
  }, 750)

  onSearchInput = (event: Event) => {
    if (this.args.setTextFilter) {
      this.args.setTextFilter(event as Event & { target: HTMLInputElement })
    }

    this.debouncedSendTeacherEvent()
  }

  <template>
    <div
      data-test-progress-table
      class="progress-table"
      {{applyTransform (perform this.transformItems (or @items (array)) this.pipelineDescriptors)}}
    >
      <TabGroupComponent
        class="gap-2"
        @theme={{themeKey @subscriptionType}}
        @tabType={{TAB_TYPE_KEYS.borderClassic}}
        data-test-select-product
        as |tabGroup|
      >
        {{#each this.productTabs as |product|}}
          <tabGroup.linkToTab @query={{hash product=product}}>
            {{t (concat "products." product)}}
          </tabGroup.linkToTab>
        {{/each}}
      </TabGroupComponent>
      <BasicTable
        class="w-full"
        exportable={{@exportable}}
        @columns={{this.columns}}
        @rows={{this.transformedItems}}
        @config={{hash sortingConfig=@sortingConfig onSortClick=@onSortClick}}
        as |tableContent|
      >
        <tableContent.head as |headContent|>
          {{#let (concat "table-box table-box-header " this.colorClassNames.groupedHeaderBg) as |headerClass|}}
            {{! Custom header with controls }}
            <tr>
              <th colspan={{this.columns.length}} class={{headerClass}}>
                <div class="flex items-center">
                  {{#let (uniqueId) as |uniqueId|}}
                    <label for="class-filter-{{uniqueId}}" class="mr-2 font-normal">
                      {{t "manage.manageProgress.controls.dropdownLabel"}}
                    </label>
                    <ProgressTableGroupFilter
                      id="class-filter-{{uniqueId}}"
                      @isSubco={{this.isSubco}}
                      @allSchoolClasses={{@allSchoolClasses}}
                      @mySchoolClasses={{this.mySchoolClasses}}
                      @groupFilterOption={{this.groupFilterOption}}
                      @setGroupFilterOption={{this.setGroupFilterOption}}
                    />
                  {{/let}}
                  <InputText
                    data-test-text-filter
                    class="ml-auto font-normal"
                    type="search"
                    role="searchbox"
                    value={{@textFilter}}
                    placeholder={{t "manage.manageProgress.controls.searchPlaceholder"}}
                    aria-label={{t "manage.manageProgress.controls.searchPlaceholder"}}
                    @themeKey={{themeKey @subscriptionType}}
                    @icon="search"
                    @iconEnd={{true}}
                    {{on "input" this.onSearchInput}}
                  />
                </div>
              </th>
            </tr>
            {{! Custom grouped header }}
            <tr data-test-grouped-header-row>
              <th colspan={{this.columns.length}} class="{{headerClass}} text-left">
                <UiButton
                  data-test-batch-edit-button
                  class="font-normal"
                  disabled={{this.isNoneSelected}}
                  @appearance={{ButtonAppearance.muted}}
                  {{on "click" (optional @onBatchEditClick)}}
                >
                  {{t "tables.progress.headers.controls.editProgressButton"}}
                </UiButton>
              </th>
            </tr>
          {{/let}}
          {{! Standard header row }}
          <headContent.row as |rowContent cellMeta|>
            {{#if cellMeta.columnObject.isSelectColumn}}
              {{! Select all checkbox header cell }}
              <rowContent.cell>
                {{t "tables.progress.headers.selectAll"}}
                <input
                  data-test-select-all-checkbox
                  aria-label={{t "tables.progress.headers.selectAll"}}
                  type="checkbox"
                  checked={{this.isAllSelected}}
                  class="cursor-pointer"
                  {{on "input" (fn (optional @onItemSelectAll) (if this.isAllSelected (array) this.selectableItems))}}
                />
              </rowContent.cell>
            {{else}}
              {{! Standard cells }}
              <rowContent.cell />
            {{/if}}
          </headContent.row>
        </tableContent.head>
        {{! If not transforming and no rows to show, always only display the empty message }}
        {{#if (and this.transformItems.isIdle (not this.transformedItems))}}
          <tableContent.spanner class="p-3 text-center italic">
            {{t "manage.noDataAvailable"}}
          </tableContent.spanner>
        {{else}}
          {{! If transforming, always show a loading spinner }}
          {{! When no items, show it normally. }}
          {{! If there are items, place it on top of the existing table body (absolute) }}
          {{#if this.transformItems.isRunning}}
            <tableContent.spanner
              class="table-box table-box-cell border-transparent
                {{if this.transformedItems 'absolute left-1/2 -translate-x-1/2 transform'}}"
            >
              <FidgetSpinnerWaveComponent @small={{true}} @centered={{true}} />
            </tableContent.spanner>
          {{/if}}
          <tableContent.body class={{if this.transformItems.isRunning "opacity-50"}} as |bodyContent rowMeta|>
            {{#let (includes this.selectedItemIds rowMeta.rowObject.id) as |isRowSelected|}}
              {{! 'group' row class is responsible for the row hover color, which we don't want when row is expanded }}
              <bodyContent.row
                class="group {{if isRowSelected 'is-selected'}} table-row-striped"
                as |rowContent cellMeta|
              >
                {{#if cellMeta.columnObject.isSelectColumn}}
                  {{! Select checkbox cell }}
                  <rowContent.cell {{on "click" (fn (optional @onItemSelect) rowMeta.rowObject)}}>
                    <input
                      data-test-select-checkbox
                      type="checkbox"
                      checked={{isRowSelected}}
                      class="cursor-pointer"
                      aria-label={{t "tables.progress.controls.checkboxAria"}}
                    />
                  </rowContent.cell>
                {{else if cellMeta.columnObject.isEditColumn}}
                  {{! Edit button cell }}
                  <rowContent.cell>
                    <UiButton
                      data-test-edit-button
                      @appearance={{ButtonAppearance.muted}}
                      {{on "click" (fn (optional @onItemButtonClick) rowMeta.rowObject)}}
                    >
                      <PhosphorIcon @icon="phosphor-note-pencil-fill" />
                      <span>{{t "tables.progress.controls.expandButton.edit"}}</span>
                    </UiButton>
                  </rowContent.cell>
                {{else}}
                  {{! Standard cells }}
                  <rowContent.cell />
                {{/if}}
              </bodyContent.row>
            {{/let}}
          </tableContent.body>
        {{/if}}
      </BasicTable>

      <BasePageSelector class="my-4" @pageCount={{this.pageCount}} @itemCount={{@items.length}} />
    </div>
  </template>
}

export default ProgressTable

/**
 * Pipeline function that filters out items that are not in the group filter, but will not filter out selected items
 *
 * @param {Array<Object>} items - the items in the pipeline
 * @param {Function|Array<Object>|String} groupFilter - A function to use as a filter, or an array of school class
 * objects from a dropdown selection, or a string that is a school class id
 */
function groupFilterFunction({ groupFilter, isItemSelected }: GroupFilterFnArgs): FilterPredicate {
  // Create a filter function that returns true if student is in any of the school classes from the group filter
  const filterFunction = (student: FilterDataItem) =>
    (student as StudentViewModel).schoolClasses.some((sc) => groupFilter.includes(sc))

  // When filtering by the group, make sure to keep selected rows too.
  return (item) => isItemSelected(item) || filterFunction(item)
}

// This creates and returns a filtering function, to be used in a pipeline item filter.
// The created function will filter out items that do not match the text filter search term, but keep selected items
function textFilterFunction({ textFilter, dataKeysToFilterOn, isItemSelected }: TextFilterFnArgs): FilterPredicate {
  // Split into lowercased keywords
  const filterKeywords = textFilter.toLowerCase().split(' ')

  // A function that takes a data item, gets data using the keys, returning all data from the item as a single string
  // that can be matched against
  const getDataToFilterOnFromDataItem: (item: FilterDataItem) => string = (dataItem) => {
    const dataStringsToFilterOn = dataKeysToFilterOn.map((key) => {
      const dataFromItem = dataItem[key] ?? ''
      return typeof dataFromItem === 'string' || typeof dataFromItem === 'number' ? `${dataFromItem}` : ''
    })
    return dataStringsToFilterOn.join(' ').toLowerCase()
  }

  // For a given data item, this function returns true if at least one keyword is present in the data
  const includeDataItem: FilterPredicate = (dataItem) => {
    const searchData = getDataToFilterOnFromDataItem(dataItem)
    return filterKeywords.some((keyword) => searchData.match(keyword))
  }

  // When filtering data items by the text, make sure to keep selected items too.
  return (item) => isItemSelected(item) || includeDataItem(item)
}
